import { IPmListFilterRaw } from 'PreventionPackage/MainModule/interfaces/IPmListFilterRaw';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import {
  knmFilterFormBooleanSelectOptions,
  knmFilterFormSignedSelectOptions,
} from 'KnmListPackage/KnmListPanelModule/constants/knmListFilterOptions';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { ICountrySelectOption } from 'CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import { IGenericReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IGenericReasonSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryTargetEntitySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryTargetEntitySelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';

import { IPmListFilter } from '../interfaces/IPmListFilter';
import { erknmPublishedStatusesSelectOptions } from '../../../CorePackage/MainModule/constants/erknmPublishedStatuses';
import { IKnmFilterForm } from '../../../KnmListPackage/KnmListPanelModule/interfaces/IKnmFilterFormSecondary';
import { ITerritorialUnitSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';

interface IMapRawToPmFilter {
  raw: IPmListFilterRaw;
  organisationsSelectOptions: Array<IDictionaryKnoOrganizationSelectOption>;
  supervisionTypesSelectOptions: Array<IDictionarySupervisionTypeSelectOption>;
  pmStatusesSelectOptions: IKnmStatusSelectOption[];
  countriesSelectOptions: ICountrySelectOption[];
  reasonsSelectOptions: IGenericReasonSelectOption[];
  targetEntitiesSelectOptions: IDictionaryTargetEntitySelectOption[] | undefined;
  subjectTypesSelectOptions: IDictionarySubjectTypeSelectOption[] | undefined;
  subjectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  subjectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined;
  riskCategoriesSelectOptions: IDictionaryRiskCategorySelectOption[] | undefined;
  dangerClassesSelectOptions: IDictionaryDangerClassSelectOption[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
}

export function mapRawToPmFilter(data: IMapRawToPmFilter): IPmListFilter {
  const {
    raw,
    organisationsSelectOptions,
    supervisionTypesSelectOptions,
    pmStatusesSelectOptions,
    countriesSelectOptions,
    reasonsSelectOptions,
    targetEntitiesSelectOptions,
    subjectTypesSelectOptions,
    subjectKindsSelectOptions,
    subjectSubKindsSelectOptions,
    riskCategoriesSelectOptions,
    dangerClassesSelectOptions,
    kindsSelectOptions,
    territorialUnitsSelectOptions,
  } = data;
  const statusesFiltered = pmStatusesSelectOptions.filter(selectOption => {
    return raw?.statusIds?.some(statusId => statusId === selectOption?.id);
  });
  const countriesFiltered = countriesSelectOptions.filter(selectOption => {
    return raw?.subjectCountryIds?.some(countryId => countryId === selectOption?.id);
  });
  const reasonTypesFiltered = reasonsSelectOptions.filter(selectOption => {
    return raw?.reasonIds?.some(reasonId => reasonId === selectOption?.id);
  });
  const targetEntitiesFiltered = targetEntitiesSelectOptions?.filter(selectOption =>
    raw?.subjectTypeIds?.some(subjectTypeId => subjectTypeId === selectOption.id),
  );
  const subjectKindsFiltered = subjectKindsSelectOptions?.filter(selectOption => {
    return raw?.objectKindIds?.some(objectKindId => objectKindId === selectOption?.id);
  });
  const subjectSubKindsFiltered = subjectSubKindsSelectOptions?.filter(selectOption => {
    return raw?.objectSubKindIds?.some(objectSubKindId => objectSubKindId === selectOption?.id);
  });
  const pubStatusesFiltered = erknmPublishedStatusesSelectOptions?.filter(selectOption =>
    raw?.pubStatuses?.some(pubStatus => pubStatus === selectOption.value),
  );
  const subjectTypesFiltered = subjectTypesSelectOptions?.filter(selectOption =>
    raw?.objectTypeIds?.some(objectTypeId => objectTypeId === selectOption.id),
  );
  const supervisionType = supervisionTypesSelectOptions.find(
    (supervisionTypeSelectOption: IDictionarySupervisionTypeSelectOption) =>
      supervisionTypeSelectOption?.id === raw?.supervisionTypeId,
  );

  return {
    name: raw?.name,
    years:
      raw?.years?.map(year => ({
        id: String(year),
        label: String(year),
        value: String(year),
      })) || [],
    domains: raw?.domainIds?.reduce((domains: IKnmFilterForm['domains'], domainId) => {
      const territorialUnitSelectOption = territorialUnitsSelectOptions.find(item => item.id === domainId);

      if (territorialUnitSelectOption) {
        const res = domains ?? [];

        res.push(territorialUnitSelectOption);

        return res;
      }

      return domains;
    }, undefined),
    includeDomainChild: raw?.includeDomainChild || false,
    kinds:
      kindsSelectOptions &&
      kindsSelectOptions.filter(kindsSelectOption => raw?.kinds?.some(kind => kind === kindsSelectOption.label)),
    controllingOrganizations: organisationsSelectOptions?.filter(
      (userKnoOrganisationSelectOption: IDictionaryKnoOrganizationSelectOption) =>
        raw?.controllingOrganizationIds?.some(
          controllingOrganizationId => Number(controllingOrganizationId) === Number(userKnoOrganisationSelectOption.id),
        ),
    ),
    includeChildKno: raw?.includeChildKno || false,
    ignoreKno: raw?.ignoreKno || false,

    supervisionType,
    supervisionTypeId: supervisionType?.code,
    ...(raw?.startDateBetween?.[0] && {
      startDateBetweenStart: formatDate({ date: raw?.startDateBetween?.[0], dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    ...(raw?.startDateBetween?.[1] && {
      startDateBetweenEnd: formatDate({ date: raw?.startDateBetween?.[1], dateFormat: DATE_FORMAT_LONG_FNS }),
    }),
    hasObjectionWarning: knmFilterFormBooleanSelectOptions.find(
      selectOption => selectOption.value === raw?.hasObjectionWarning,
    ),
    reasons: targetEntitiesFiltered && targetEntitiesFiltered.length ? reasonTypesFiltered : undefined,
    subjectTypes: subjectTypesFiltered && subjectTypesFiltered.length ? subjectTypesFiltered : undefined,
    subjectOrgName: raw?.subjectOrgName,
    subjectOgrn: raw?.subjectOgrn,
    subjectInn: raw?.subjectInn,
    subjectKpp: raw?.subjectKpp,
    subjectOkveds: raw?.subjectOkveds,
    subjectSnils: raw?.subjectSnils,
    subjectCountries: countriesFiltered && countriesFiltered.length ? countriesFiltered : undefined,
    objectAddress: raw?.objectAddress,
    objectTypes: subjectTypesFiltered && subjectTypesFiltered.length ? subjectTypesFiltered : undefined,
    objectKinds: subjectKindsFiltered && subjectKindsFiltered.length ? subjectKindsFiltered : undefined,
    objectSubKinds: subjectSubKindsFiltered && subjectSubKindsFiltered.length ? subjectSubKindsFiltered : undefined,
    objectRiskCategory:
      riskCategoriesSelectOptions &&
      riskCategoriesSelectOptions.find(selectOption => selectOption.id === raw?.objectRiskCategoryId),
    objectDangerClass:
      dangerClassesSelectOptions &&
      dangerClassesSelectOptions.find(selectOption => selectOption.id === raw?.objectDangerClassId),
    isHasCollaboratingOrganization: knmFilterFormBooleanSelectOptions.find(
      selectOption => selectOption.value === raw?.isHasCollaboratingOrganization,
    ),
    inspectorFullName: raw?.inspectorFullName,
    signed: knmFilterFormSignedSelectOptions.find(selectOption => selectOption.value === raw?.signed),
    statuses: statusesFiltered.length ? statusesFiltered : undefined,
    pubStatuses: pubStatusesFiltered.length ? pubStatusesFiltered : undefined,
    erpId: raw?.erpId,
    deletedInclude: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedInclude),
    deletedOnly: knmFilterFormBooleanSelectOptions.find(selectOption => selectOption.value === raw?.deletedOnly),
  };
}
